import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select'; 
import { 
  Box, Paper, Typography, TextField, Button, Grid, Alert, Snackbar, 
  Autocomplete, Divider, Chip, CircularProgress, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { 
  Save as SaveIcon, AddCircle as AddIcon, ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon, Edit as EditIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';


const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// ================= AUTH FETCH =================
const authFetch = async (url: string, options: any = {}) => {
  const token = localStorage.getItem('token');

  const res = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  return res;
};

// ================= INTERFACES =================
interface Patient {
  id: string;
  name: string;
  lastName: string;
  cedula?: string;
  idNumber?: string;
}

interface ExamenOption {
  value: number;
  label: string;
  unit?: string;
  graficar?: boolean;
  valor_min?: number | string;
  valor_max?: number | string;
  valor_referencia_text?: string;
}

interface CategoriaExamen {
  label: string;
  options: ExamenOption[];
}

const ParaclinicosPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados principales
  const [pacienteActivo, setPacienteActivo] = useState<Patient | null>(null);
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [menuOptions, setMenuOptions] = useState<CategoriaExamen[]>([]); 
  const [fullCatalog, setFullCatalog] = useState<CategoriaExamen[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Carga Temporal
  const [selectedExamen, setSelectedExamen] = useState<ExamenOption | null>(null);
  const [valorInput, setValorInput] = useState('');
  const [fechaEstudio, setFechaEstudio] = useState(() => {
    const hoy = new Date();
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    const fechaLocal = `${year}-${month}-${day}`;
    console.log('📅 Fecha inicial del sistema:', fechaLocal);
    return fechaLocal;
  });
  const [bandejaTemporal, setBandejaTemporal] = useState<any[]>([]);

  // Historial y Gráficas
  const [historialData, setHistorialData] = useState<any[]>([]);
  const [graficasSeleccionadas, setGraficasSeleccionadas] = useState<string[]>([]);
  
  // Diálogos y UI
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingExam, setEditingExam] = useState<ExamenOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({open: false, message: '', severity: 'success' as 'success' | 'error'});

  const COLORS = ["#1976d2", "#4caf50", "#ff9800", "#f44336", "#9c27b0"];

  // 1. Carga inicial sincronizada
  const fetchData = async () => {
    try {
      const [patRes, catRes] = await Promise.all([
        authFetch(`${API_URL}/api/patients`),
        authFetch(`${API_URL}/api/paraclinicos/catalogo`) // ← Trae TODO el catálogo
      ]);

      if (patRes.ok) {
        const patients: Patient[] = await patRes.json();
        setAllPatients(patients);
      }

      if (catRes.ok) {
        const fullCatData: CategoriaExamen[] = await catRes.json();
        setFullCatalog(fullCatData);
        console.log('📚 Catálogo completo cargado:', fullCatData.length, 'categorías');
        
        const graficables = fullCatData
          .flatMap(g => g.options || [])
          .filter(o => o.graficar === true);
        console.log('✅ Variables graficables:', graficables.length);
      }

      // NO recuperar sesión - siempre iniciar vacío
      sessionStorage.removeItem('pacienteActivo');
      console.log('✅ Iniciando con selector de paciente vacío');
    } catch (error) {
      console.error("Error cargando datos iniciales", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 2. Cargar historial
  const cargarHistorial = async (id: string) => {
    try {
      const res = await authFetch(
        `${API_URL}/api/paraclinicos/historial/${id}`
      );

      if (res.status === 500) {
        console.warn('⚠️ Backend devolvió 500, historial vacío');
        setHistorialData([]);
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setHistorialData(Array.isArray(data) ? data : []);

    } catch (e) {
      console.error('❌ Error cargando historial:', e);
      setHistorialData([]);
    }
  };

  // 3. Opciones para el buscador de gráficas (SIEMPRE usa catálogo completo)
  const opcionesParaGrafica = useMemo(() => {
    const opciones = fullCatalog
      .flatMap(g => g.options || [])
      .filter(o => o.graficar === true);
    
    // Eliminar duplicados por label
    const unicos = opciones.reduce((acc: ExamenOption[], curr: ExamenOption) => {
      if (!acc.find((item: ExamenOption) => item.label === curr.label)) {
        acc.push(curr);
      }
      return acc;
    }, []);
    
    return unicos;
  }, [fullCatalog]);

  // 4. Buscador de catálogo (Resultados específicos)
  useEffect(() => {
    const delay = setTimeout(async () => {
      if (searchQuery.length > 1) {
        const res = await authFetch(`${API_URL}/api/paraclinicos/catalogo?q=${searchQuery}`);
        if (res.ok) setMenuOptions(await res.json());
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const agregarABandeja = () => {
    if (!selectedExamen || !valorInput) {
      console.log('❌ Falta examen o valor');
      return;
    }
    
    console.log('➕ Agregando a bandeja:', {
      examen: selectedExamen.label,
      valor: valorInput,
      fecha: fechaEstudio
    });
    
    const nuevoItem = {
        examenId: selectedExamen.value,
        nombre: selectedExamen.label,
        valor: valorInput,
        fecha: fechaEstudio,
        unit: selectedExamen.unit
    };
    
    setBandejaTemporal([...bandejaTemporal, nuevoItem]);
    setValorInput('');
    setSelectedExamen(null);
    
    console.log('✅ Item agregado. Bandeja actual:', [...bandejaTemporal, nuevoItem]);
  };

  const handleSaveAll = async () => {
    if (!pacienteActivo || bandejaTemporal.length === 0) return;
    
    const cantidadItems = bandejaTemporal.length;
    console.log('💾 Iniciando guardado de', cantidadItems, 'resultados');
    
    setLoading(true);
    
    try {
        const res = await authFetch(`${API_URL}/api/paraclinicos/resultados`, {
          method: 'POST',
          body: JSON.stringify({
            pacienteId: pacienteActivo.id,
            examenes: bandejaTemporal
          })
        });
        
        console.log('📡 Respuesta del servidor:', res.status);
        
        if (res.ok) {
            const result = await res.json();
            console.log('✅ Servidor confirmó guardado:', result);
            
            setBandejaTemporal([]);
            
            const hoy = new Date();
            const year = hoy.getFullYear();
            const month = String(hoy.getMonth() + 1).padStart(2, '0');
            const day = String(hoy.getDate()).padStart(2, '0');
            setFechaEstudio(`${year}-${month}-${day}`);
            
            await new Promise(resolve => setTimeout(resolve, 300));
            
            console.log('🔄 Recargando historial del paciente:', pacienteActivo.id);
            await cargarHistorial(pacienteActivo.id);
            
            setNotification({ 
                open: true, 
                message: `✅ ${cantidadItems} resultado(s) guardado(s) y sincronizados`, 
                severity: 'success' 
            });
            
        } else {
            const errorData = await res.json();
            console.error('❌ Error del servidor:', errorData);
            setNotification({ 
                open: true, 
                message: 'Error: ' + (errorData.error || 'Desconocido'), 
                severity: 'error' 
            });
        }
        
    } catch (e) { 
        console.error('❌ Error en handleSaveAll:', e);
        setNotification({ 
            open: true, 
            message: 'Error de conexión al servidor', 
            severity: 'error' 
        }); 
    } finally {
        setLoading(false);
        console.log('🏁 Proceso de guardado finalizado');
    }
  };

  const handleUpdateCatalog = async () => {
    if (!editingExam) return;
    try {
      const res = await authFetch(`${API_URL}/api/paraclinicos/catalogo/${editingExam.value}`, {
        method: 'PUT',
        body: JSON.stringify(editingExam)
      });
      if (res.ok) {
        setOpenEditDialog(false);
        setNotification({ open: true, message: 'Catálogo actualizado', severity: 'success' });
        const catRes = await authFetch(`${API_URL}/api/paraclinicos/catalogo`);
        if (catRes.ok) setFullCatalog(await catRes.json());
      }
    } catch (error) {
      setNotification({ open: true, message: 'Error al actualizar catálogo', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>Laboratorio y Paraclínicos</Typography>
          <Box>
            <Button startIcon={<EditIcon/>} onClick={() => setOpenEditDialog(true)} sx={{ mr: 1 }}>Catálogo</Button>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} color="inherit">Volver</Button>
          </Box>
      </Box>

      <Grid container spacing={3}>
        {/* GRÁFICA */}
        <Grid item xs={12}>
            <Card elevation={3}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6"><TimelineIcon color="primary" sx={{ mr: 1 }}/> Evolución Médica</Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                            {opcionesParaGrafica.length} variables disponibles
                          </Typography>
                          <Autocomplete 
                              multiple 
                              size="small" 
                              options={opcionesParaGrafica} 
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) => option.label === value.label}
                              value={opcionesParaGrafica.filter(opt => graficasSeleccionadas.includes(opt.label))}
                              onChange={(_, newValue) => {
                                console.log('🎯 Gráficas seleccionadas:', newValue.map(v => v.label));
                                setGraficasSeleccionadas(newValue.map(v => v.label));
                              }}
                              filterOptions={(options, state) => {
                                const inputValue = state.inputValue.toLowerCase();
                                return options.filter(option => 
                                  option.label.toLowerCase().includes(inputValue)
                                );
                              }}
                              renderInput={(p) => <TextField {...p} label="Variables a graficar" sx={{ minWidth: 280 }} />}
                              noOptionsText="No hay variables graficables"
                          />
                        </Box>
                    </Box>
                    <Box sx={{ height: 350, mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={(() => {
                                const puntosGrafica: any[] = [];
                                
                                historialData.forEach(grupo => {
                                    graficasSeleccionadas.forEach(variable => {
                                        Object.keys(grupo).forEach(clave => {
                                            if (clave.startsWith(variable) && clave.includes('(Examen:')) {
                                                const match = clave.match(/\(Examen: ([^)]+)\)/);
                                                if (match) {
                                                    const fechaExamen = match[1];
                                                    const valor = String(grupo[clave]).split(' (Examen:')[0].trim();
                                                    const numerico = parseFloat(valor);
                                                    
                                                    if (!isNaN(numerico)) {
                                                        let punto = puntosGrafica.find(p => p.fecha === fechaExamen);
                                                        if (!punto) {
                                                            punto = { fecha: fechaExamen };
                                                            puntosGrafica.push(punto);
                                                        }
                                                        
                                                        if (!punto[variable]) {
                                                            punto[variable] = numerico;
                                                        }
                                                    }
                                                }
                                            }
                                        });
                                    });
                                });
                                
                                puntosGrafica.sort((a, b) => {
                                    const [dA, mA, yA] = a.fecha.split('/');
                                    const [dB, mB, yB] = b.fecha.split('/');
                                    return new Date(yA, mA - 1, dA).getTime() - new Date(yB, mB - 1, dB).getTime();
                                });
                                
                                console.log('📊 Puntos para gráfica:', puntosGrafica);
                                
                                return puntosGrafica;
                            })()}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis 
                                    dataKey="fecha" 
                                    tick={{ fontSize: 12 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis />
                                <ChartTooltip />
                                <Legend />
                                {graficasSeleccionadas.map((v, i) => (
                                    <Line 
                                        key={v} 
                                        type="monotone" 
                                        dataKey={v} 
                                        stroke={COLORS[i % COLORS.length]} 
                                        strokeWidth={3} 
                                        connectNulls 
                                        dot={{ r: 4 }} 
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            </Card>
        </Grid>

        <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderLeft: '5px solid #1976d2' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Paciente Seleccionado</Typography>
                <Autocomplete 
                    options={allPatients} 
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    getOptionLabel={(o) => `${o.name} ${o.lastName} (${o.cedula || o.idNumber || ''})`}
                    value={pacienteActivo}
                    onChange={(_, v) => { 
                      setPacienteActivo(v); 
                      if(v) {
                        sessionStorage.setItem('pacienteActivo', JSON.stringify(v));
                        setGraficasSeleccionadas([]);
                        cargarHistorial(v.id); 
                      }
                    }}
                    renderInput={(p) => <TextField {...p} label="Cambiar Paciente" size="small" />}
                />
            </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Registrar Resultados</Typography>
                <Select 
                  isClearable={true} 
                  placeholder="Escriba el nombre del examen..."
                  onInputChange={v => setSearchQuery(v)} 
                  options={menuOptions} 
                  onChange={v => setSelectedExamen(v)} 
                  value={selectedExamen} 
                />

                {selectedExamen && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #cde4ff' }}>
                        <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                            <strong>📅 Fecha del Examen:</strong> Indique cuándo se realizó el laboratorio (puede ser anterior a hoy)
                        </Alert>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <TextField 
                                fullWidth 
                                label={`Valor (${selectedExamen.unit || 'u'})`} 
                                value={valorInput} 
                                onChange={e => setValorInput(e.target.value)} 
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField 
                                fullWidth 
                                type="date" 
                                label="📆 Fecha del Examen" 
                                InputLabelProps={{ shrink: true }} 
                                value={fechaEstudio} 
                                onChange={e => {
                                  console.log('📅 Fecha del examen seleccionada:', e.target.value);
                                  setFechaEstudio(e.target.value);
                                }}
                                helperText="Fecha cuando se tomó la muestra"
                              />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <Button 
                                fullWidth 
                                variant="contained" 
                                startIcon={<AddIcon />} 
                                onClick={agregarABandeja} 
                                sx={{ height: 55 }}
                              >
                                Añadir a lista
                              </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <Box sx={{ mt: 3, p: 2, border: '2px dashed #e0e0e0', borderRadius: 2, minHeight: 100, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {bandejaTemporal.length === 0 && <Typography color="text.disabled" sx={{ m: 'auto' }}>No hay exámenes en espera</Typography>}
                    {bandejaTemporal.map((item, i) => (
                        <Chip key={i} label={`${item.nombre}: ${item.valor} (${item.fecha})`} onDelete={() => setBandejaTemporal(bandejaTemporal.filter((_, idx) => idx !== i))} color="primary" variant="outlined" />
                    ))}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="contained" color="success" size="large" 
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                      disabled={bandejaTemporal.length === 0 || loading} 
                      onClick={handleSaveAll}
                    >
                      Guardar en Historia
                    </Button>
                </Box>
            </Paper>
        </Grid>
      </Grid>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Catálogo de Rangos</DialogTitle>
          <DialogContent dividers>
              <Autocomplete 
                options={fullCatalog.flatMap(g => g.options || [])} 
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                getOptionLabel={o => o.label || ''} 
                onChange={(_, v) => setEditingExam(v ? {...v} : null)} 
                renderInput={p => <TextField {...p} label="Buscar examen para editar" sx={{ mt: 1 }}/>} 
              />
              {editingExam && (
                  <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" color="primary" fontWeight="bold">{editingExam.label}</Typography>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={6}><TextField fullWidth label="Mín" type="number" value={editingExam.valor_min || ''} onChange={e => setEditingExam({...editingExam, valor_min: e.target.value})}/></Grid>
                          <Grid item xs={6}><TextField fullWidth label="Máx" type="number" value={editingExam.valor_max || ''} onChange={e => setEditingExam({...editingExam, valor_max: e.target.value})}/></Grid>
                          <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Referencia Texto" value={editingExam.valor_referencia_text || ''} onChange={e => setEditingExam({...editingExam, valor_referencia_text: e.target.value})}/></Grid>
                      </Grid>
                  </Box>
              )}
          </DialogContent>
          <DialogActions>
              <Button onClick={() => setOpenEditDialog(false)}>Cerrar</Button>
              <Button variant="contained" onClick={handleUpdateCatalog} disabled={!editingExam}>Actualizar</Button>
          </DialogActions>
      </Dialog>

      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({...notification, open: false})}>
        <Alert severity={notification.severity} variant="filled">{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ParaclinicosPage;